import React, { useState } from 'react';

import { Box, Button, Grid, IconButton, Tooltip } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddCircle from '@material-ui/icons/AddCircle';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import HelpIcon from '@material-ui/icons/Help';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import { Table, Card } from 'components/UI';
import { useStyles } from './Common.styled';
import i18n from 'i18n-js';
import { VariableBufferModal } from './VariableBufferModal';
import VariableBufferDeleteMessage from './VariableBufferDeleteMessage';
import { checkSameBuffer, compareBuffer, unitsPlural } from 'utils';
import { useConfirm } from 'hooks';
import theme from 'theme';

export const ReservationRulesetVariableBufferWidget = (props) => (
  <Card title={i18n.t('reservation_rulesets.maintenance_buffer')}>
    <Box>
      <ReservationRulesetVariableBuffer {...props} />
    </Box>
  </Card>
);

export const ReservationRulesetVariableBuffer = ({ ruleset, loading, setFieldValue, editable }) => {
  const initialVariableBuffer = {
    buffer: null,
    currentIndex: null,
  };
  const classes = useStyles();
  const [showVariableBufferModal, setShowVariableBufferModal] = useState(false);
  const [currentVariableBuffer, setCurrentVariableBuffer] = useState(initialVariableBuffer);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [error, setError] = useState(null);
  const [lastDeleted, setLastDeleted] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleAccordionToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleOnAdd = () => {
    setError(null);
    setShowVariableBufferModal((state) => !state);
    setCurrentVariableBuffer(initialVariableBuffer);
  };

  const handleOnEdit = (row, index) => {
    const prevRow =
      index > 0 ? ruleset?.driver_rules?.multiple_maintenance_buffer[index - 1] : null;
    const startMagnitude = prevRow?.time_range?.magnitude || 0;
    const startUnits = prevRow?.time_range
      ? unitsPlural(prevRow.time_range.magnitude, prevRow.time_range.units)
      : '';

    setCurrentVariableBuffer({
      buffer: row,
      currentIndex: index,
      startMagnitude: startMagnitude,
      startUnits: startUnits,
    });
    setError(null);
    setShowVariableBufferModal(true);
    setLastDeleted(null);
  };

  const handleRemoveBuffer = (index) => {
    const oldBuffers = ruleset?.driver_rules?.multiple_maintenance_buffer;
    const newBuffers = [...oldBuffers];
    newBuffers.splice(index, 1);
    setFieldValue('driver_rules.multiple_maintenance_buffer', newBuffers);
    setLastDeleted(index);
  };

  const handleAddBuffer = (newBuffer) => {
    const oldBuffers = ruleset?.driver_rules?.multiple_maintenance_buffer;
    if (oldBuffers.filter((buffer) => checkSameBuffer(buffer, newBuffer)).length > 0) {
      setError(i18n.t('reservation_rulesets.same_range_buffer_error'));
    } else {
      setError(null);
      const newBuffers = [...oldBuffers, newBuffer];
      newBuffers.sort(compareBuffer);
      setFieldValue('driver_rules.multiple_maintenance_buffer', newBuffers);
      handleOnAdd();
      setLastDeleted(null);
    }
  };

  const handleEditBuffer = (index, newBuffer) => {
    const oldBuffers = ruleset?.driver_rules?.multiple_maintenance_buffer;
    if (
      oldBuffers.filter((buffer, idx) => idx != index && checkSameBuffer(buffer, newBuffer))
        .length > 0
    ) {
      setError(i18n.t('reservation_rulesets.same_range_buffer_error'));
    } else {
      setError(null);
      const newBuffers = [...oldBuffers];
      newBuffers[index] = newBuffer;
      newBuffers.sort(compareBuffer);
      console.log(newBuffers, oldBuffers);
      setFieldValue('driver_rules.multiple_maintenance_buffer', newBuffers);
      handleOnAdd();
    }
  };

  const handleClosePopover = () => {
    setLastDeleted(null);
  };

  const [toggleDeleteRR, DeleteRRConfirm] = useConfirm({
    title: i18n.t('reservation_rulesets.delete_buffer'),
    message: i18n.t('reservation_rulesets.confirm_delete_buffer'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: () => {
      handleRemoveBuffer(currentIndex);
      setCurrentIndex(null);
    },
    onCancel: () => {
      setCurrentIndex(null);
    },
  });

  const actionsRenderer = (row, index) => {
    return (
      <Box display="flex" alignItems="center">
        {row.is_default ? <div style={{ width: 48 }} /> : null}
        <IconButton
          onClick={() => {
            handleOnEdit(row, index);
          }}
        >
          <EditIcon />
        </IconButton>

        {!row.is_default ? (
          <IconButton
            onClick={() => {
              setCurrentIndex(index);
              toggleDeleteRR();
            }}
            data-cy="delete-item-btn"
          >
            <DeleteIcon />
          </IconButton>
        ) : null}
      </Box>
    );
  };

  const columns = [
    {
      name: (
        <span className={classes.header}>
          {i18n.t('reservation_rulesets.reservation_lengths')}
          <Tooltip
            title={i18n.t('reservation_rulesets.time_range_tooltip')}
            placement="top"
            aria-label="help"
          >
            <HelpIcon color="primary" className={classes.helpIcon} />
          </Tooltip>
        </span>
      ),
      selector: 'time_range',
      cell: (row, index) => {
        const prevRow =
          index > 0 ? ruleset?.driver_rules?.multiple_maintenance_buffer[index - 1] : null;
        const startMagnitude = prevRow?.time_range?.magnitude || 0;
        const startUnits = prevRow?.time_range
          ? unitsPlural(prevRow.time_range.magnitude, prevRow.time_range.units, true)
          : '';
        return (
          <Tooltip
            classes={{ tooltip: classes.tooltip }}
            title={<VariableBufferDeleteMessage onClose={handleClosePopover} />}
            placement="right"
            aria-label="help"
            interactive
            arrow
            open={lastDeleted === index}
            onClose={handleClosePopover}
          >
            {row?.is_default ? (
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ display: 'block' }}>
                  <Box display="flex" alignItems="center">
                    <Box fontWeight="fontWeightBold">
                      {i18n.t('reservation_rulesets.default', {
                        start_magnitude: startMagnitude,
                        start_units: startUnits,
                        magnitude: row?.time_range?.magnitude,
                        units: unitsPlural(
                          row?.time_range?.magnitude,
                          row?.time_range?.units,
                          true
                        ),
                      })}
                    </Box>
                    <Tooltip
                      title={i18n.t('reservation_rulesets.time_range_fmt_default_tooltip')}
                      placement="top"
                      aria-label="help"
                    >
                      <HelpIcon color="primary" className={classes.helpIcon} />
                    </Tooltip>
                  </Box>
                </span>
              </span>
            ) : (
              <span>
                {i18n.t('reservation_rulesets.time_range_fmt', {
                  start_magnitude: startMagnitude,
                  start_units: startUnits,
                  magnitude: row?.time_range?.magnitude,
                  units: unitsPlural(row?.time_range?.magnitude, row?.time_range?.units, true),
                }) || '--'}
              </span>
            )}
          </Tooltip>
        );
      },
      maxWidth: '18rem',
      grow: 1,
    },
    {
      name: (
        <>
          <span style={{ display: 'block' }}>
            {i18n.t('reservation_rulesets.time_range_for_techs_reservation_line1')}
          </span>
          <span style={{ display: 'block' }}>
            {i18n.t('reservation_rulesets.time_range_for_techs_reservation_line2')}
          </span>
        </>
      ),
      selector: 'maintenance_buffer',
      cell: (row) => (
        <span
          data-cy="time-range-text"
          className={row.is_default ? classes.defaultDisclaimer : null}
        >
          {i18n.t('reservation_rulesets.maintenance_buffer_fmt', {
            magnitude: row?.buffer?.magnitude,
            units: unitsPlural(row?.buffer?.magnitude, row?.buffer?.units, true),
          }) || '--'}
        </span>
      ),
      grow: 1,
    },
    ...(editable
      ? [
          {
            name: i18n.t('tableColumns.actions'),
            selector: 'actions',
            cell: actionsRenderer,
            grow: 0,
            minWidth: '9rem',
            center: true,
          },
        ]
      : []),
  ];

  return (
    <Grid container direction="row" spacing={2}>
      <Grid item xs={12}>
        <span>{i18n.t('reservation_rulesets.maintenance_buffer_description')}</span>
      </Grid>
      <Grid item xs={12}>
        <Accordion expanded={isExpanded} onChange={handleAccordionToggle}>
          <AccordionSummary
            classes={{
              root: isExpanded ? classes.accordionSummaryRoot : null,
              content: classes.accordionSummaryContent,
            }}
            expandIcon={<ExpandMoreIcon />}
          >
            <span className={classes.bufferCalculation}>
              <InfoOutlinedIcon style={{ marginRight: 20 }} />
              <Box fontWeight="fontWeightBold">
                {i18n.t('reservation_rulesets.buffer_calculation')}
              </Box>
            </span>
          </AccordionSummary>
          <AccordionDetails>
            <span
              className={classes.bufferCalculationDetails}
              dangerouslySetInnerHTML={{
                __html: i18n.t('reservation_rulesets.buffer_calculation_details'),
              }}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item xs={12}>
        <Box mb={2}>
          <Table
            data-cy="rrs-variable-buffer-table"
            data={ruleset?.driver_rules?.multiple_maintenance_buffer}
            columns={columns}
            loading={loading}
            pagination={false}
            conditionalRowStyles={[
              {
                when: (row) => row.is_default,
                style: {
                  backgroundColor: `${theme.palette.background.input} !important`,
                },
              },
            ]}
          />
        </Box>
      </Grid>
      {editable ? (
        <Grid container spacing={1}>
          <Button
            className={classes.addBufferButton}
            color="primary"
            startIcon={<AddCircle />}
            onClick={handleOnAdd}
          >
            {i18n.t('reservation_rulesets.add_buffer')}
          </Button>
          {showVariableBufferModal && (
            <VariableBufferModal
              currentVariableBuffer={currentVariableBuffer}
              open={showVariableBufferModal}
              handleClick={handleOnAdd}
              handleAddBuffer={handleAddBuffer}
              handleEditBuffer={handleEditBuffer}
              error={error}
            />
          )}
        </Grid>
      ) : null}
      <DeleteRRConfirm />
    </Grid>
  );
};
